import type { AdminUser, UserPermission } from "@ied/shared-types";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { SERVICE_API } from "@/lib/consts";

type CheckAuthResponse = {
	authenticated: boolean;
	user?: AdminUser;
};

const PUBLIC_ROUTES = ["/login"];

async function checkAuth(): Promise<CheckAuthResponse> {
	const res = await fetch(`${SERVICE_API}/api/auth`, {
		credentials: "include",
		headers: {
			"Content-Type": "application/json",
		},
	});

	if (!res.ok) {
		throw new Error("Auth check failed");
	}

	return res.json();
}

export function useAuthInfo() {
	const location = useLocation();
	const navigate = useNavigate();

	const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);

	const { data, isLoading, isError } = useQuery({
		queryKey: ["auth"],
		queryFn: checkAuth,
		retry: false,
	});

	if (isError && !isPublicRoute) {
		navigate("/login");
	}

	return {
		isAuthed: data?.authenticated ?? false,
		isLoading,
		user: data,
		hasPermission: (permission: UserPermission) => {
			if (!data?.user?.permissions) return false;
			// Admin permission grants access to everything
			if (data.user.permissions.includes("admin")) return true;
			return data.user.permissions.includes(permission);
		},
	};
}
